import { deepEqual } from '@/utils/function'
import { useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { CreateProcessFormData } from './index'

/**
 * Save draft cho thẻ "Process Details" ở form tạo phiếu (react-hook-form).
 * Chỉ persist 5 trường Process Details vào localStorage; KHÔNG lưu type,
 * source/destination, items/importItems. Khi F5/mở lại sẽ hỏi khôi phục.
 *
 * Mirror pattern của Asset (use-asset-draft-persistence.ts), adapt cho RHF:
 * save = watch subset, restore = form.reset(merge subset).
 */

// Chỉ các trường thuộc thẻ Process Details.
type ProcessDetailsDraft = Pick<
    CreateProcessFormData,
    'process_date' | 'expected_date' | 'reason' | 'description' | 'notes'
>

const pickDetails = (data: CreateProcessFormData): ProcessDetailsDraft => ({
    process_date: data.process_date,
    expected_date: data.expected_date,
    reason: data.reason,
    description: data.description,
    notes: data.notes,
})

interface UseProcessDraftPersistenceParams {
    form: UseFormReturn<CreateProcessFormData>
    defaultValues: CreateProcessFormData
    storageKey: string
    /** Chỉ bật ở chế độ tạo mới (không bật cho edit). */
    enabled: boolean
}

export const useProcessDraftPersistence = ({
    form,
    defaultValues,
    storageKey,
    enabled,
}: UseProcessDraftPersistenceParams) => {
    const [shouldRestore, setShouldRestore] = useState(false)
    const draftRef = useRef<ProcessDetailsDraft | null>(null)
    const initializedRef = useRef(false)

    // Watch từng field (primitive) để auto-save chỉ chạy khi giá trị thực đổi.
    const processDate = form.watch('process_date')
    const expectedDate = form.watch('expected_date')
    const reason = form.watch('reason')
    const description = form.watch('description')
    const notes = form.watch('notes')

    // Khi mount: kiểm tra draft đã lưu (1 lần, chỉ create mode).
    useEffect(() => {
        if (!enabled || initializedRef.current) return
        initializedRef.current = true

        const saved = localStorage.getItem(storageKey)
        if (!saved) return

        try {
            const parsed = JSON.parse(saved) as ProcessDetailsDraft
            if (deepEqual(parsed, pickDetails(defaultValues))) {
                localStorage.removeItem(storageKey)
                return
            }
            draftRef.current = parsed
            setShouldRestore(true)
        } catch {
            localStorage.removeItem(storageKey)
        }
    }, [enabled, storageKey, defaultValues])

    // Auto-save mỗi khi 1 trong 5 field đổi (bỏ qua khi đang chờ user quyết định khôi phục).
    useEffect(() => {
        if (!enabled || shouldRestore) return
        const current = pickDetails(form.getValues())
        // Không ghi khi form vẫn nguyên default: vừa tránh ghi rác, vừa tránh
        // đè draft thật ở lần render đầu (trước khi shouldRestore kịp bật).
        if (deepEqual(current, pickDetails(defaultValues))) return
        try {
            localStorage.setItem(storageKey, JSON.stringify(current))
        } catch (e) {
            console.warn('Không thể lưu draft phiếu', e)
        }
    }, [
        enabled,
        shouldRestore,
        storageKey,
        form,
        defaultValues,
        processDate,
        expectedDate,
        reason,
        description,
        notes,
    ])

    const acceptRestore = () => {
        if (draftRef.current) {
            // Merge subset draft vào giá trị hiện tại, giữ nguyên các field khác.
            form.reset(
                { ...form.getValues(), ...draftRef.current },
                { keepDefaultValues: true }
            )
        }
        draftRef.current = null
        setShouldRestore(false)
    }

    const declineRestore = () => {
        localStorage.removeItem(storageKey)
        draftRef.current = null
        setShouldRestore(false)
    }

    const clearDraft = () => {
        try {
            localStorage.removeItem(storageKey)
        } catch (e) {
            console.warn('Không thể xóa draft phiếu', e)
        }
    }

    return { shouldRestore, acceptRestore, declineRestore, clearDraft }
}
